"use client";

import { clsx } from "clsx";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import posthog from "posthog-js";
import { logout } from "@/lib/api/auth";
import { writeCachedAuthUser } from "@/lib/auth/clientAuthCache";
import { registerPosthogSuperProperties } from "@/lib/analytics/posthogConsent";
import { readConsent } from "@/lib/consent";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";

type Props = {
  user: { name: string; email: string };
};

export function UserAvatarMenu({ user }: Props) {
  const router = useRouter();

  async function handleLogout() {
    try {
      await logout();
      writeCachedAuthUser(null);
      posthog.reset(); // clear the identified user; next events will be from a new anonymous session
      // reset() wipes all super-properties — re-register immediately so subsequent events in
      // this tab (which never re-mounts PostHogInit, a client-side navigation
      // follows) don't silently lose cookie_consent / environment.
      registerPosthogSuperProperties(posthog, readConsent(), {
        environment: process.env.NEXT_PUBLIC_POSTHOG_ENVIRONMENT,
      });
      router.push("/");
      router.refresh();
    } catch (err) {
      console.error("logout failed", err);
      toast.error("Couldn't log out. Try again.");
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className={clsx(
            "inline-flex items-center justify-center h-9 w-9 rounded-full",
            "border border-border bg-surface-muted text-text-muted shadow-[var(--shadow-sm)]",
            "transition-colors hover:bg-surface hover:text-primary hover:border-border-strong",
            "focus-ring",
          )}
        >
          <svg
            aria-hidden="true"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <div className="px-4 py-2 text-xs text-text-muted truncate max-w-[200px]">
          {user.name || user.email}
        </div>
        <DropdownMenuSeparator className="my-1 h-px bg-border" />
        <DropdownMenuItem asChild>
          <Link href="/users/my-results">My Results</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 h-px bg-border" />
        <DropdownMenuItem onSelect={handleLogout}>Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
